// The llama page: a wood-framed handheld (device.ts) whose glass shows the
// room. Join a room by its code, then three tabs, on keys along the bottom of
// the glass, under the row of emotes. Both rows hold still on every screen.
// Count holds the viewer's hologram llama, the line and the guess. Mood holds
// employer satisfaction. Tips holds the room's
// leaderboard. A tab is rebuilt only when what it shows changes. Countdowns
// tick in place.

import { cleanName, NAME_LENGTH_MOST, parseRoomCode } from '../protocol';
import { buildActivity, liveActivity } from './activities/activity';
import { keycap, plank } from './chrome';
import { makeDevice } from './device';
import { buzz, el, readStore, writeStore } from './dom';
import { emoteRow, emotesOpen } from './emotes';
import { makeHologram } from './hologram';
import { hueColor } from './hues';
import { identityRow } from './identity';
import { makeMeter } from './meter';
import { watchMore } from './more';
import type { Page, Tab } from './page';
import { pixelText, preloadFonts } from './pixelText';
import { roomCodeButton } from './roomLink';
import { say } from './say';
import { isMuted, play, setMuted, unlockOnFirstTouch } from './sfx';
import { type LlamaSocket, openLlamaSocket } from './socket';
import {
  applyMessage,
  freshState,
  type LlamaState,
  type Screen,
  screenOf,
  shownTips,
} from './state';
import { STRINGS } from './strings';
import { buildBoard } from './tabs/board';
import { buildCount, countAction, tipLine } from './tabs/count';
import { buildMood, updateMood } from './tabs/mood';
import { showTurnstile } from './turnstile';
import { keepAwake } from './wakeLock';
import './style.css';

const TICK_MS = 200;
const tokenKey = (code: string): string => `llamallink:token:${code}`;
// The hologram on the join form, and in the identity row once in a room.
const JOIN_HERO_SCALE = 4;
const IDENTITY_SCALE = 2;
// Three times the identity row's llama: the whole body is its stage.
const STAGE_SCALE = 6;
const METER_SCALE = 8;
const TITLE_SCALE = 4;
const TOAST_MS = 3500;
const PAD_OPEN_BUZZ_MS = 40;
const LEAVE_ARMED_MS = 3000;
// The llinked pop sits a little higher than the pad's.
const LLINKED_PITCH = 1.25;
const TABS: Tab[] = ['count', 'mood', 'board'];

const device = makeDevice();
(document.querySelector<HTMLDivElement>('#app') as HTMLDivElement).append(device.root);
// Everything the page shows is on the device's glass.
const app = device.screen;
const OSD_SCALE = 2;
const CODE_LETTERS = 4;

let state: LlamaState = freshState();
let socket: LlamaSocket | null = null;
let roomCode = '';
let tab: Tab = 'count';
let shownKey = '';
let lastScreenKind = '';
// The run screen last shown (its end, or a new run starting), so each buzzes once.
let runTurnShown: Screen['kind'] | null = null;
let wasOnStream = false;
// Room time of the newest tip already shown as a toast.
let toastedAt = 0;
let ticking = false;

const page: Page = {
  state: () => state,
  send: (msg) => socket?.send(msg),
  hologram: makeHologram(IDENTITY_SCALE),
  meter: makeMeter(METER_SCALE),
};
// The llama standing in the empty glass while it waits for a round. It is
// its own hologram, so the identity row's small one keeps its motion.
const stageHologram = makeHologram(STAGE_SCALE);

// crypto.randomUUID exists only on a secure page (HTTPS or localhost). A phone
// on the LAN during dev loads the page over plain http, where getRandomValues
// still works.
const randomToken = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const tokenFor = (code: string): string => {
  const kept = readStore(tokenKey(code));
  if (kept) return kept;
  const made = randomToken();
  writeStore(tokenKey(code), made);
  return made;
};

// ---- Joining ----------------------------------------------------------------

const NAME_KEY = 'llamallink:name';

function showJoin(code: string, error = ''): void {
  shownKey = 'join';
  const form = el('form', 'join');
  page.hologram.setMode('on');
  page.hologram.setScale(JOIN_HERO_SCALE);
  const entry = slotEntry({
    value: code,
    length: CODE_LETTERS,
    label: STRINGS.codeLabel,
    code: true,
  });
  const name = slotEntry({
    value: readStore(NAME_KEY),
    length: NAME_LENGTH_MOST,
    label: STRINGS.nameLabel,
    code: false,
  });
  const join = plank(STRINGS.join, () => form.requestSubmit());
  form.append(
    pixelText(STRINGS.title, 'compass', TITLE_SCALE, '#e7d5b3', 'title'),
    page.hologram.canvas,
    say(STRINGS.codeLabel, 'note'),
    entry.box,
    say(STRINGS.nameLabel, 'note'),
    name.box,
    join,
  );
  if (error) form.append(say(error, 'warn'));
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const parsed = parseRoomCode(entry.input.value);
    if (!parsed) {
      showJoin(entry.input.value, STRINGS.badCode);
      return;
    }
    writeStore(NAME_KEY, cleanName(name.input.value));
    enterRoom(parsed);
  });
  app.replaceChildren(form);
}

interface SlotEntryOptions {
  value: string;
  length: number;
  label: string;
  // The room code: four wide slots, capitals only. Otherwise a name: narrow
  // slots, as typed.
  code: boolean;
}

// Text as a row of keycap slots on the glass, one letter each. A real input
// lies over them, unseen, so the phone's own keyboard types into it.
function slotEntry(options: SlotEntryOptions): { box: HTMLElement; input: HTMLInputElement } {
  const { value, length, label, code } = options;
  const box = el('label', 'code-entry');
  const slots = el('div', 'code-slots');
  const input = el('input', 'code-input');
  input.setAttribute('aria-label', label);
  input.value = value;
  input.maxLength = length;
  input.autocomplete = 'off';
  input.autocapitalize = code ? 'characters' : 'words';
  input.spellcheck = false;
  const slotClass = code ? 'code-slot' : 'code-slot name-slot';
  const scale = code ? OSD_SCALE * 2 : OSD_SCALE;
  const show = (): void => {
    const shown = code ? input.value.toUpperCase() : input.value;
    const letters = shown.padEnd(length, ' ').slice(0, length);
    slots.replaceChildren(
      ...[...letters].map((letter, at) => {
        const slot = el('span', at === input.value.length ? `${slotClass} is-next` : slotClass);
        if (letter.trim()) slot.append(pixelText(letter, 'buzzer', scale, '#ece8ff'));
        return slot;
      }),
    );
  };
  input.addEventListener('input', () => {
    buzz();
    play('tick');
    show();
  });
  show();
  box.append(slots, input);
  return { box, input };
}

function enterRoom(code: string): void {
  window.history.replaceState(null, '', `/${code}`);
  roomCode = code;
  state = freshState();
  toastedAt = 0;
  socket = openLlamaSocket(
    code,
    tokenFor(code),
    cleanName(readStore(NAME_KEY)),
    (msg) => {
      state = applyMessage(state, msg, Date.now());
      render();
    },
    (link) => {
      state = { ...state, link };
      render();
    },
  );
  if (!ticking) {
    ticking = true;
    window.setInterval(render, TICK_MS);
  }
  render();
}

function leaveRoom(): void {
  socket?.send({ t: 'leave' });
  socket?.close();
  socket = null;
  keepAwake(false);
  showJoin('');
}

// ---- The shell --------------------------------------------------------------

function render(): void {
  // Out of any room (the join form is up): nothing here to draw.
  if (socket === null) return;
  const screen = screenOf(state, Date.now());
  document.documentElement.style.setProperty('--llama', hueColor(state.hue));
  keepAwake(state.seat !== null && screen.kind !== 'ended');
  followStage(screen);
  const key = tabKey(screen);
  if (key !== shownKey) {
    shownKey = key;
    const bare = screen.kind === 'ended' || screen.kind === 'challenge';
    const focus = isFocus(screen);
    // The action row is there only while it can hold something: the plank
    // round the counter, the emotes after the lock-in, a bare screen's leave.
    // Everywhere else the body takes it.
    const noAction = !bare && (!focus || screen.kind === 'activity');
    app.classList.toggle('is-bare', bare);
    app.classList.toggle('is-focus', focus);
    app.classList.toggle('is-stage', onStage(screen));
    app.classList.toggle('no-action', noAction);
    app.replaceChildren(
      osd(screen),
      identityRow(page, screen, IDENTITY_SCALE),
      watchMore(bodyFor(screen)),
      ...(noAction ? [] : [actionFor(screen)]),
      ...(focus ? [] : controls(screen)),
    );
  }
  updateLive(screen);
  toastTips(screen);
}

// The focus layout, for the moments the game itself holds the thumb: the
// sheep run, the pad, the guess locked in, and a party activity. The identity
// row shrinks to the name, and the emotes and the tabs leave the glass, so
// the one control of the moment has the room. After the lock-in the emotes
// come back in the action row, where the plank was. The Count tab is forward
// on all of these (followStage), so no tab is lost.
const FOCUS_ON: ReadonlySet<Screen['kind']> = new Set(['counting', 'pad', 'locked', 'activity']);
const isFocus = (screen: Screen): boolean => FOCUS_ON.has(screen.kind);

// The rows along the bottom of the glass: the emotes over the tab keys. A
// screen with no tabs has neither.
function controls(screen: Screen): HTMLElement[] {
  if (screen.kind === 'ended' || screen.kind === 'challenge') return [];
  return [emoteRow(page, emotesOpen(screen)), tabKeys()];
}

// The action slot: the one plank of the moment, or nothing. One height on
// every screen, so the rows above never move.
function actionFor(screen: Screen): HTMLElement {
  const slot = el('div', 'act');
  if (screen.kind === 'ended' || screen.kind === 'challenge') {
    slot.append(plank(STRINGS.leave, leaveRoom));
    return slot;
  }
  if (tab !== 'count') return slot;
  if (screen.kind === 'locked') {
    slot.append(emoteRow(page, emotesOpen(screen)));
    return slot;
  }
  const action = countAction(page, screen);
  if (action) slot.append(action);
  return slot;
}

// When the sheep start to run, and again when the pad opens, the Count tab
// comes forward, wherever the viewer was.
// The moment the viewer's llama reaches the stream, the phone buzzes once.
function followStage(screen: Screen): void {
  const comesForward =
    screen.kind === 'counting' || screen.kind === 'pad' || screen.kind === 'activity';
  if (comesForward && screen.kind !== lastScreenKind) {
    tab = 'count';
    buzz(PAD_OPEN_BUZZ_MS);
    play('pop');
  }
  // A run's end, and the start of the next, buzz once as they arrive.
  const runTurn = screen.kind === 'run_over' || (screen.kind === 'hold' && screen.starting);
  if (runTurn && runTurnShown !== screen.kind) {
    buzz(PAD_OPEN_BUZZ_MS);
    play('pop');
  }
  runTurnShown = runTurn ? screen.kind : null;
  const onStream = screen.kind === 'waiting' && screen.onStream;
  if (onStream && lastScreenKind === 'waiting' && !wasOnStream) {
    buzz(PAD_OPEN_BUZZ_MS);
    play('pop', LLINKED_PITCH);
  }
  wasOnStream = onStream;
  lastScreenKind = screen.kind;
}

// Everything that should rebuild the screen. Countdowns update in place.
function tabKey(screen: Screen): string {
  // A screen with no tabs is rebuilt only when it changes.
  if (screen.kind === 'ended' || screen.kind === 'challenge') return JSON.stringify(screen);
  const { secondsLeft: _left, ...rest } = screen as Screen & { secondsLeft?: number | null };
  const shell = [
    tab,
    state.link,
    state.paused,
    state.hostOnline,
    state.hue,
    isMuted(),
    emotesOpen(screen),
  ];
  if (tab === 'mood') return JSON.stringify([shell, state.mood]);
  if (tab === 'board') return JSON.stringify([shell, state.board, state.standing]);
  return JSON.stringify([shell, rest, shownTips(state, Date.now()).at(-1)?.showAt]);
}

// The body row: the open tab's content, from the top of the row.
function bodyFor(screen: Screen): HTMLElement {
  if (screen.kind === 'ended') {
    const box = el('div', 'body');
    box.append(say(STRINGS.ended[screen.why]));
    return box;
  }
  if (screen.kind === 'challenge') return buildChallenge(screen.siteKey);
  if (screen.kind === 'activity' && tab === 'count') return buildActivity(page, screen.activity);
  if (tab === 'mood') return wrap(buildMood(page));
  if (tab === 'board') return wrap(buildBoard(page));
  if (onStage(screen)) return stageBody();
  return buildCount(page, screen);
}

// Seated and between rounds, the Count tab has nothing to ask. The llama
// stands in the glass instead, beaming, and the identity row drops its own.
const onStage = (screen: Screen): boolean =>
  tab === 'count' && (screen.kind === 'waiting' || screen.kind === 'hold');

function stageBody(): HTMLElement {
  const body = el('div', 'body body-stage');
  stageHologram.setColor(hueColor(state.hue));
  stageHologram.setMode('on');
  body.append(stageHologram.canvas);
  return body;
}

function wrap(content: HTMLElement): HTMLElement {
  const box = el('div', 'body');
  box.append(content);
  return box;
}

function buildChallenge(siteKey: string): HTMLElement {
  const box = el('div', 'body');
  const holder = el('div', 'challenge');
  box.append(say(STRINGS.challenge), holder);
  showTurnstile(holder, siteKey, (token) => socket?.send({ t: 'proof', token }));
  return box;
}

// The glass's top line, as a set's on-screen display: the room and its
// state on the left, sound and leave on two small dark keys on the right.
function osd(screen: Screen): HTMLElement {
  const bar = el('header', 'osd');
  const room = el('div', 'osd-room');
  room.append(roomCodeButton(roomCode, app));
  if (state.link === 'reconnecting') room.append(say(STRINGS.reconnecting, 'warn'));
  else if (!state.hostOnline) room.append(say(STRINGS.hostAway, 'warn'));
  else if (state.paused) room.append(say(STRINGS.paused, 'note'));
  const keys = el('div', 'osd-keys');
  keys.append(
    smallKey(isMuted() ? STRINGS.soundOff : STRINGS.soundOn, () => {
      setMuted(!isMuted());
      play('click');
      render();
    }),
    leaveKey(screen),
  );
  bar.append(room, keys);
  return bar;
}

// In a round, a stray thumb on leave would drop the llama's seat, so the
// first press only arms it: the key's word asks, and a second press inside
// LEAVE_ARMED_MS leaves. Out of a round, one press leaves.
function leaveKey(screen: Screen): HTMLButtonElement {
  const inRound = state.seat !== null && isFocus(screen);
  let armedUntil = 0;
  const key = smallKey(STRINGS.leave, () => {
    if (!inRound || Date.now() < armedUntil) {
      leaveRoom();
      return;
    }
    armedUntil = Date.now() + LEAVE_ARMED_MS;
    showWord(STRINGS.leaveSure);
    window.setTimeout(() => showWord(STRINGS.leave), LEAVE_ARMED_MS);
  });
  const word = key.querySelector<HTMLElement>('.key-word');
  const showWord = (text: string): void => {
    if (word) word.textContent = text.toLowerCase();
  };
  return key;
}

// A word on a key, in the page's monospace face.
const keyWord = (text: string): HTMLElement => {
  const word = el('span', 'key-word');
  word.textContent = text;
  return word;
};

const smallKey = (label: string, onPress: () => void): HTMLButtonElement =>
  keycap({
    label,
    face: 'dark',
    content: keyWord(label.toLowerCase()),
    className: 'key-small',
    onPress,
  });

// The tabs along the bottom of the glass. The open tab hangs from the rule
// above the row as a lit panel, capped in the viewer's llama colour. It
// switches on the press, not the release, like a key.
function tabKeys(): HTMLElement {
  const bar = el('nav', 'tab-keys');
  bar.setAttribute('role', 'tablist');
  for (const each of TABS) {
    const on = each === tab;
    const choose = (): void => {
      if (tab === each) return;
      tab = each;
      play('click', 1.15);
      buzz();
      render();
    };
    const node = el('button', on ? 'tab is-open' : 'tab', STRINGS.tabs[each]);
    node.type = 'button';
    node.setAttribute('role', 'tab');
    node.setAttribute('aria-selected', String(on));
    node.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      choose();
    });
    // A keyboard or switch user presses with Enter or Space, which is a click.
    node.addEventListener('click', (event) => {
      if (event.detail === 0) choose();
    });
    bar.append(node);
  }
  return bar;
}

function updateLive(screen: Screen): void {
  const left = 'secondsLeft' in screen ? screen.secondsLeft : null;
  for (const countdown of app.querySelectorAll<HTMLElement>('.countdown')) {
    showCountdown(countdown, left);
  }
  if (screen.kind === 'activity') liveActivity(page, app, screen.activity);
  if (tab === 'mood') updateMood(page, app);
}

function showCountdown(holder: HTMLElement, left: number | null): void {
  const text = left === null ? '' : STRINGS.closing(left);
  if (holder.textContent !== text) holder.textContent = text;
}

// A tip the stream has reached pops up over the glass, once.
// The result on the Count tab says the tip itself, so there it only buzzes.
function toastTips(screen: Screen): void {
  const tip = shownTips(state, Date.now()).at(-1);
  if (!tip || tip.showAt <= toastedAt) return;
  toastedAt = tip.showAt;
  buzz();
  if (tab === 'count' && screen.kind === 'result') return;
  const toast = el('div', 'toast');
  toast.setAttribute('role', 'status');
  toast.append(tipLine(tip.share));
  app.append(toast);
  window.setTimeout(() => toast.remove(), TOAST_MS);
}

// ---- Start ------------------------------------------------------------------

preloadFonts();
unlockOnFirstTouch();
const fromPath = parseRoomCode(window.location.pathname.replace(/^\//, ''));
// A link with a room code joins at once. Without one, the page asks for the code.
if (fromPath) {
  enterRoom(fromPath);
} else {
  showJoin('');
}
